import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RegistrationQueue = ({
  requests,
  loading,
  selectedUser,
  onUserSelect,
  onApprove,
  onReject,
  getRoleLabel
}) => {
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_approval: { label: 'قيد المراجعة', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
      approved: { label: 'موافق عليه', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
      rejected: { label: 'مرفوض', color: 'bg-red-500/20 text-red-400 border-red-500/30' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending_approval;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-8">
        <div className="flex flex-col items-center justify-center py-12">
          <Icon name="Loader2" size={40} className="animate-spin text-primary mb-4" />
          <p className="text-muted-foreground">جاري تحميل الطلبات...</p>
        </div>
      </div>
    );
  }

  if (requests?.length === 0) {
    return (
      <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-8">
        <div className="text-center py-12">
          <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground text-lg">لا توجد طلبات تسجيل</p>
          <p className="text-sm text-muted-foreground mt-2">جميع الطلبات تمت معالجتها أو لا توجد طلبات جديدة</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Icon name="List" size={20} color="#3B82F6" />
          قائمة الطلبات ({requests?.length})
        </h3>
      </div>
      <div className="space-y-3 max-h-[calc(100vh-400px)] overflow-y-auto pr-2">
        {requests?.map(request => (
          <div
            key={request?.id}
            onClick={() => onUserSelect(request)}
            className={`bg-card/50 backdrop-blur-sm border rounded-lg p-4 transition-all cursor-pointer ${
              selectedUser?.id === request?.id
                ? 'border-primary shadow-lg shadow-primary/20'
                : 'border-border hover:border-primary/50'
            }`}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex items-center gap-3 flex-1">
                <div className="p-3 bg-primary/20 rounded-lg">
                  <Icon name="User" size={24} color="#3B82F6" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-white text-lg">{request?.full_name}</h4>
                  <p className="text-sm text-muted-foreground">{request?.email}</p>
                </div>
              </div>
              {getStatusBadge(request?.approval_status)}
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-background/50 rounded-lg">
              <div>
                <p className="text-xs text-muted-foreground mb-1">الدور الوظيفي</p>
                <div className="flex items-center gap-2">
                  <Icon name="Briefcase" size={14} color="#3B82F6" />
                  <p className="text-sm font-medium text-white">
                    {getRoleLabel(request?.role)}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">رقم الهاتف</p>
                <div className="flex items-center gap-2">
                  <Icon name="Phone" size={14} color="#10B981" />
                  <p className="text-sm font-medium text-white">
                    {request?.phone || 'غير متوفر'}
                  </p>
                </div>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-muted-foreground mb-1">تاريخ التسجيل</p>
                <div className="flex items-center gap-2">
                  <Icon name="Calendar" size={14} color="#F59E0B" />
                  <p className="text-sm font-medium text-white">
                    {formatDate(request?.created_at)}
                  </p>
                </div>
              </div>
            </div>

            {/* Approval Info */}
            {request?.approval_status === 'approved' && request?.approved_at && (
              <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg mb-3">
                <div className="flex items-center gap-2 text-sm text-green-400">
                  <Icon name="CheckCircle" size={16} />
                  <span>تمت الموافقة في {formatDate(request?.approved_at)}</span>
                </div>
              </div>
            )}

            {request?.approval_status === 'rejected' && request?.rejection_reason && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg mb-3">
                <div className="flex items-start gap-2">
                  <Icon name="XCircle" size={16} className="text-red-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-400 mb-1">سبب الرفض:</p>
                    <p className="text-sm text-red-300">{request?.rejection_reason}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {request?.approval_status === 'pending_approval' && (
              <div className="flex gap-2 pt-3 border-t border-border">
                <Button
                  onClick={(e) => {
                    e?.stopPropagation();
                    onApprove(request?.id);
                  }}
                  variant="default"
                  iconName="Check"
                  className="flex-1"
                >
                  موافقة
                </Button>
                <Button
                  onClick={(e) => {
                    e?.stopPropagation();
                    onReject(request);
                  }}
                  variant="destructive"
                  iconName="X"
                  className="flex-1"
                >
                  رفض
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default RegistrationQueue;